import type { Definition } from '@exodus/dependency-types';
import type { AddressProvider } from '@passkeys/address-provider';
import type { TransactionSigner } from '@passkeys/transaction-signer';
import type { Transactions } from '@passkeys/transactions';

import type { Web3 } from '@/background/features/web3/module.js';
import type { Asset } from '@/types/index.js';
import type { BlinkActionsAtom } from './atoms/actions.js';
import type { AppsAtom } from './atoms/apps.js';
import { handleEthereumTransaction } from './transactions/ethereum.js';
import { handleSolanaTransaction } from './transactions/solana.js';
import type { App } from './types.js';

export type BlinkModuleDependencies = {
  blinkActionsAtom: BlinkActionsAtom;
  appsAtom: AppsAtom;
  addressProvider: AddressProvider;
  transactions: Transactions;
  transactionSigner: TransactionSigner;
  web3: Web3;
};

const createBlinks = ({
  blinkActionsAtom,
  appsAtom,
  addressProvider,
  transactions,
  transactionSigner,
  web3,
}: BlinkModuleDependencies) => {
  const saveAction = (action: Omit<App, 'date'>) =>
    blinkActionsAtom.set((actions = {}) => ({
      ...actions,
      [action.url]: {
        ...action,
        date: new Date(),
      },
    }));

  const handleClientErrorResponse = async (response: Response) => {
    const contentType = response.headers.get('Content-Type');
    const isJson = contentType?.includes('application/json');

    if (isJson) {
      return response.json();
    }

    const error = await response.text();
    return { error };
  };

  const fetchTransaction = async ({
    url,
    assetName,
    walletAccount,
  }: {
    url: string | URL;
    assetName: string;
    walletAccount: string;
  }) => {
    const { address: fromAddress } = await addressProvider.getReceiveAddress({
      assetName,
      walletAccount,
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ account: fromAddress }),
    });

    if (response.status === 400) {
      return handleClientErrorResponse(response);
    }

    if (!response.ok) {
      throw new Error('Oops! Something went wrong, please try again.');
    }

    const data = await response.json();
    return data;
  };

  const executeAction = async ({
    url,
    asset,
    walletAccount,
  }: {
    url: string;
    asset: Asset;
    walletAccount: string;
  }) => {
    try {
      const { transaction, error, ...success } = await fetchTransaction({
        url,
        assetName: asset.name,
        walletAccount,
      });

      if (error) {
        throw error;
      }

      let txId: string | undefined;

      if (asset.assetType.includes('SOLANA')) {
        const result = await handleSolanaTransaction({
          asset,
          transaction,
          walletAccount,
          transactions,
          transactionSigner,
        });
        txId = result.txId;
      }

      if (
        asset.assetType.includes('ETHEREUM') ||
        asset.assetType.includes('ERC20')
      ) {
        const result = await handleEthereumTransaction({
          transaction,
          web3,
        });
        txId = result.txId;
      }

      if (!txId) {
        throw new Error('Could not execute action.');
      }

      return { txId, success };
    } catch (error: any) {
      const { message } = error;

      return {
        success: false,
        error: message ?? String(error),
      };
    }
  };

  const loadApps = async () => {
    const response = await fetch(
      `${__BLOCKCHAIN_ACTIONS_SERVER_URL__}/api/apps`,
    );
    const apps = (await response.json()) as App[];

    appsAtom.set(apps);
  };

  return {
    saveAction,
    executeAction,
    loadApps,
  };
};

const blinksDefinition = {
  id: 'blinks',
  type: 'module',
  factory: createBlinks,
  dependencies: [
    'blinkActionsAtom',
    'appsAtom',
    'addressProvider',
    'transactions',
    'transactionSigner',
    'web3',
  ],
} as const satisfies Definition;

export type Blinks = ReturnType<typeof blinksDefinition.factory>;

export default blinksDefinition;
